import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { inputBaseClasses } from "@mui/material/InputBase";
import {
  Clear,
  CheckBoxOutlineBlankRounded,
  CheckBoxRounded,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useModals } from "../../store/dialogs/hooks";
import { useSnackbars } from "../../store/snackbars/hooks";
import { Transition } from "../../pages/app/utils/animation";
import { queueMergeClipsRequest } from "../../pages/app/utils/dataLoader";
import { useEffect, useState } from "react";
import { EDITING } from "../../store/statuses";

const CombineClipsSchema = z.object({
  combinedClipName: z
    .string()
    .trim()
    .nonempty({ message: "A name for the combined clip is required." }),
  selectedClips: z
    .array(z.number())
    .min(1, { message: "A minimum of two clips is required." }),
});

export const CombineClipsDialog = ({
  caseId,
  alignedClips,
  selectedAlignedClip,
}) => {
  const theme = useTheme();
  const { closeCombineClipsDialog, showCombineClipsDialog } = useModals();
  const { openCombineClipsSnackbar } = useSnackbars();
  const [selectedClipName, setSeletedClipName] = useState(null);
  const [isCombining, setIsCombining] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    resetField,
    trigger,
    formState: { errors, isValid },
    watch,
  } = useForm({
    resolver: zodResolver(CombineClipsSchema),
    defaultValues: {
      combinedClipName: "",
      selectedClips: [],
    },
    mode: "all",
  });

  const selectedClips = watch("selectedClips");

  const handleCancel = () => {
    reset();
    closeCombineClipsDialog();
  };

  const onSubmit = async (data) => {
    setIsCombining(true);
    const response = await queueMergeClipsRequest(
      caseId,
      data.combinedClipName,
      [...data.selectedClips, selectedAlignedClip],
    );
    if (response.status === "SUCCESS") {
      reset();
      closeCombineClipsDialog();
    } else if (response.status === "FAILURE") {
      resetField("selectedClips");
    }
    openCombineClipsSnackbar(response);
    setIsCombining(false);
  };

  const handleClipsClear = () => {
    resetField("selectedClips");
    trigger("selectedClips");
  };

  const handleNameClear = () => {
    resetField("combinedClipName");
    trigger("combinedClipName");
  };

  useEffect(() => {
    if (selectedAlignedClip) {
      setSeletedClipName(
        alignedClips.find((clip) => clip.id === selectedAlignedClip)
          ?.description || null,
      );
    }
  }, [alignedClips, selectedAlignedClip]);

  return (
    <Dialog
      aria-hidden={!showCombineClipsDialog}
      fullWidth
      maxWidth="xs"
      open={showCombineClipsDialog}
      onClose={handleCancel}
      aria-labelledby="combine-clips-dialog-title"
      aria-describedby="combine-clips-dialog-description"
      closeAfterTransition={false}
      slots={{
        transition: Transition,
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle id="combine-clips-dialog-title">Combine Clips</DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Controller
            name="combinedClipName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Combined Clip Name"
                margin="normal"
                error={!!errors.combinedClipName}
                helperText={
                  errors.combinedClipName
                    ? errors.combinedClipName?.message
                    : "Please enter a name for the combined clip."
                }
                required
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{
                          [`[data-shrink=false] ~ .${inputBaseClasses.root} > &`]:
                            {
                              opacity: 0,
                              pointerEvents: "none",
                            },
                        }}
                      >
                        <IconButton
                          aria-label="clear input"
                          onClick={handleNameClear}
                          edge="end"
                          size="small"
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
          <FormControl
            component="fieldset"
            error={!!errors.selectedClips}
            required
            sx={{ mb: 1 }}
          >
            <FormLabel
              component="legend"
              sx={{
                "&.Mui-focused:not(.Mui-error)": {
                  color: theme.palette.text.secondary,
                },
              }}
            >
              Select at least one clip to combine with{" "}
              <Typography component="span" fontWeight="bold">
                {selectedClipName || "the selected clip"}
              </Typography>
            </FormLabel>
            <FormGroup sx={{ width: "fit-content" }}>
              {alignedClips.map((option) =>
                option.status === EDITING ? (
                  <FormControlLabel
                    key={option.id}
                    control={
                      <Controller
                        name="selectedClips"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            icon={<CheckBoxOutlineBlankRounded />}
                            checkedIcon={
                              <CheckBoxRounded
                                color={
                                  option.id === selectedAlignedClip
                                    ? "info"
                                    : "secondary"
                                }
                              />
                            }
                            checked={
                              value.includes(option.id) ||
                              option.id === selectedAlignedClip
                            }
                            disabled={option.id === selectedAlignedClip}
                            onChange={(e) => {
                              const newValues = e.target.checked
                                ? [...value, option.id]
                                : value.filter((value) => value !== option.id);
                              onChange(newValues);
                            }}
                            sx={{
                              "&.Mui-disabled": {
                                pointerEvents: "unset",
                                cursor: "not-allowed",
                              },
                            }}
                          />
                        )}
                      />
                    }
                    label={option.description}
                  />
                ) : (
                  <Tooltip
                    key={option.id}
                    placement="top-start"
                    title="Only clips with a status of 'Editing' are available for combining."
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          icon={
                            <WarningAmberOutlined
                              color="warning"
                              sx={{ fontSize: "24px" }}
                            />
                          }
                          disabled
                        />
                      }
                      label={<Typography>{option.description}</Typography>}
                    />
                  </Tooltip>
                ),
              )}
            </FormGroup>
            <FormHelperText>
              {errors.selectedClips
                ? errors.selectedClips?.message
                : "Please select at least one additional clip."}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            disabled={!selectedClips.length}
            variant="outlined"
            onClick={handleClipsClear}
          >
            Clear Clips
          </Button>
          <Box display="flex" gap={1}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={!isValid || isCombining}
              loading={isCombining}
            >
              Combine
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
